import { Document, Page, View, Text, Image, StyleSheet, Font, Svg, Circle, Polyline, Line } from "@react-pdf/renderer";
import moment from "moment";
import segRegular from "../../../../fonts/segoeui.ttf";
import segBold from "../../../../fonts/segoeuib.ttf";
import logoEmpresa from "../PDFDocument/variants/amsa/logo.jpg";
import { __GetClientRoleCode } from "../../../../common/CommonFuctions";
import { enum__AppElementType } from "../../../../common/Dictionaries";

Font.register({
  family: "segoeui",
  fonts: [
    { src: segRegular, fontWeight: 400 },
    { src: segBold, fontWeight: 700 },
  ],
});

const COLOR_VERDE = "#92D050";
const COLOR_TEAL = "#00778B";
const COLOR_GRIS_CLARO = "#F8F8F8";
const COLOR_VERDE_TEXTO = "#00B050";
const COLOR_ROJO_TEXTO = "#C00000";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "segoeui",
    fontSize: 8,
  },
  logoRow: {
    marginBottom: 4,
  },
  logo: {
    width: 90,
    height: 34,
    objectFit: "contain",
  },
  banner: {
    backgroundColor: COLOR_VERDE,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 6,
    marginBottom: 5,
  },
  bannerTitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 13,
    textAlign: "center",
  },
  bannerSubtitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
    marginTop: 2,
  },
  headerBox: {
    flexDirection: "row",
    border: "1pt solid #000000",
    marginBottom: 6,
  },
  headerCol: {
    flex: 1,
    padding: 6,
  },
  headerColRight: {
    flex: 1,
    padding: 6,
    borderLeft: "1pt solid #000000",
  },
  fieldRow: {
    flexDirection: "row",
    marginBottom: 1,
  },
  fieldLabel: {
    fontWeight: 700,
    marginRight: 3,
    flexShrink: 0,
  },
  fieldValue: {
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 9,
    marginTop: 4,
    marginBottom: 3,
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: COLOR_TEAL,
  },
  tableHeaderCell: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 9,
    padding: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: `0.5pt solid ${COLOR_TEAL}`,
  },
  colDocumentacion: {
    flex: 4,
    padding: 3,
    fontWeight: 700,
    backgroundColor: COLOR_GRIS_CLARO,
  },
  colEstatus: {
    flex: 1.1,
    padding: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  colObservaciones: {
    flex: 4,
    padding: 3,
  },
  estatusTexto: {
    fontSize: 7,
    marginTop: 1,
  },
  notasBanner: {
    backgroundColor: COLOR_TEAL,
    padding: 3,
    marginTop: 5,
    marginBottom: 3,
  },
  notasBannerText: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
  },
  notaSubtitulo: {
    fontWeight: 700,
    fontSize: 8,
    marginTop: 2,
    marginBottom: 1,
  },
  notaBox: {
    border: "0.5pt solid #999999",
    minHeight: 14,
    padding: 3,
  },
  notaBoxTexto: {
    fontSize: 7.5,
  },
  firmaBox: {
    padding: 5,
    border: "0.5pt solid #999999",
  },
  firmaRol: {
    fontWeight: 700,
    fontSize: 9,
    marginBottom: 2,
  },
  indicesTable: {
    marginTop: 3,
    marginBottom: 4,
    marginHorizontal: 3,
    border: "0.5pt solid #999999",
  },
  indicesHeaderRow: {
    flexDirection: "row",
    backgroundColor: COLOR_GRIS_CLARO,
  },
  indicesRow: {
    flexDirection: "row",
    borderTop: "0.5pt solid #999999",
  },
  indicesHeaderCell: {
    flex: 1,
    padding: 2,
    fontSize: 7,
    fontWeight: 700,
    textAlign: "center",
    borderRight: "0.5pt solid #999999",
  },
  indicesHeaderCellLast: {
    flex: 1,
    padding: 2,
    fontSize: 7,
    fontWeight: 700,
    textAlign: "center",
  },
  indicesCell: {
    flex: 1,
    padding: 2,
    fontSize: 7,
    textAlign: "center",
    borderRight: "0.5pt solid #999999",
  },
  indicesCellLast: {
    flex: 1,
    padding: 2,
    fontSize: 7,
    textAlign: "center",
  },
});

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Datos reales del EDP (todavía vienen sin datos: checklist table/notas/firma siguen siendo mockup fase 1).
const val = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const formatFecha = (fecha) => (fecha ? moment(fecha).format("DD/MM/YYYY") : "-");

// Sin sufijo de moneda: ya existe un campo "Moneda:" propio justo debajo en el PDF.
const formatMonto = (monto) => {
  if (monto === null || monto === undefined) return "-";
  const formateado = Number(monto).toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `$ ${formateado}`;
};

// Sección "Firma Revisión": misma firma AAD/GST que ya alimenta AsesorGst/AadFechaRevision. No hay
// RUT real en este flujo de datos (ver N_EdpChecklistHandler.cs, AsesorGstUsername) — se usa el
// username como reemplazo, igual que ya hace PDFDocument/variants/default/partials/DocumentFooter.jsx.
const buildFirmaRevisionTexto = (data) => {
  if (!data?.revisionBackOfficeGst) return "-";
  const cargoTxt = data?.asesorGstCargo ? ` (${data.asesorGstCargo})` : "";
  const fechaHora = data?.aadFechaRevision ? moment(data.aadFechaRevision).format("DD/MM/YYYY HH:mm:ss") : "-";
  return `Firmado digitalmente\npor ${val(data?.asesorGst)}${cargoTxt}\n${fechaHora}`;
};

// "Mail / Notificación con entrega Estado de Pago": mensaje automático que el AEC dispara al
// enviar el EDP de Preparación a Revisión (ver N_EdpChecklistHandler.cs, FechaEnvioRevisionAec).
const buildObservacionEnvioRevisionAec = (data) =>
  data?.fechaEnvioRevisionAec
    ? `EP recibido en SG3Pay en fecha ${formatFecha(data.fechaEnvioRevisionAec)}, por el usuario ${val(data?.usuarioEnvioRevisionAec)}.`
    : "-";

// "Respaldo de ejecución del Servicio": hito de aprobación de ATO (revisión de partidas) y
// AAD/GST (revisión del EP) — ver N_EdpChecklistHandler.cs, AtoNombre/AtoFechaRevision/AadFechaRevision.
// El código de rol AAD ("GST" en AMSA) se resuelve dinámicamente con __GetClientRoleCode, igual que
// en el resto del módulo de EdP (ver ResultadoEdp.js/DocumentFooter.jsx).
const buildObservacionRespaldoEjecucion = (data, rolGst) => {
  const lineas = [];
  if (data?.atoFechaRevision) {
    lineas.push(`ATO ${val(data?.atoNombre)}, ha realizado la revisión de partidas el ${formatFecha(data.atoFechaRevision)}.`);
  }
  if (data?.aadFechaRevision) {
    lineas.push(`${rolGst} ${val(data?.asesorGst)}, ha concluido la revisión del estado de pago el ${formatFecha(data.aadFechaRevision)}.`);
  }
  return lineas.length ? lineas.join("\n") : "-";
};

// "Reajuste de tarifa": ver N_EdpChecklistHandler.cs, TieneReajuste/IndicesReajuste (reutiliza
// ResultadoHelper.getPolinomioResultado, la misma fuente que la tabla de índices del PDF actual).
const buildObservacionReajuste = (data) =>
  data?.tieneReajuste ? "Reajuste aplicado en el periodo." : "Contrato no tiene reajuste asociado.";

// Filas dinámicas de obligaciones (ScObligacion.IncluirEnChecklistEdp == true): el set de
// obligaciones y su cantidad varía por contrato/cliente, no es una lista fija de 3.
// Observaciones según el Estado que ya resuelve N_EdpChecklistHandler.cs (No Revisado/Cumple/No Cumple).
const OBSERVACION_POR_ESTADO_OBLIGACION = {
  "No Revisado": "Contrato no tiene Obligación Asignada.",
  "Cumple": "Contrato Cumple Obligación en el periodo del Estado de Pago.",
  "No Cumple": "Contrato no Cumple Obligación en el periodo del Estado de Pago.",
};

const buildObligacionesItems = (data) =>
  (data?.obligaciones ?? []).map((o) => ({
    documentacion: o.nombre,
    estatus: o.estado,
    observaciones: OBSERVACION_POR_ESTADO_OBLIGACION[o.estado] ?? val(o.estado),
  }));

const getChecklistItems = (data, rolGst) => [
  { documentacion: "Mail / Notificación con entrega Estado de Pago", estatus: "Cumple", observaciones: buildObservacionEnvioRevisionAec(data) },
  { documentacion: "Respaldo de ejecución del Servicio", estatus: "Cumple", observaciones: buildObservacionRespaldoEjecucion(data, rolGst) },
  { documentacion: "Reajuste de tarifa", estatus: "Cumple", observaciones: buildObservacionReajuste(data) },
  // "Informe mensual de KPI contractual del servicio" ya no es una fila fija (decisión del PO, 2026-10-06):
  // sale como cualquier otra obligación, solo si existe la obligación y aplica al contrato.
  ...buildObligacionesItems(data),
];

function EstatusIcon({ cumple }) {
  const color = cumple ? COLOR_VERDE_TEXTO : COLOR_ROJO_TEXTO;
  return (
    <Svg width="12" height="12" viewBox="0 0 12 12">
      <Circle cx="6" cy="6" r="6" fill={color} />
      {cumple ? (
        <Polyline points="3,6.2 5.2,8.4 9,3.6" stroke="#FFFFFF" strokeWidth={1.4} fill="none" />
      ) : (
        <>
          <Line x1="3.3" y1="3.3" x2="8.7" y2="8.7" stroke="#FFFFFF" strokeWidth={1.4} />
          <Line x1="8.7" y1="3.3" x2="3.3" y2="8.7" stroke="#FFFFFF" strokeWidth={1.4} />
        </>
      )}
    </Svg>
  );
}

// Una entrada por cada tipo del catálogo spay.edp_mensaje_tipo_checklist_pdf (data.notas, ya armado
// por el backend uniendo el catálogo con el último EdpMensaje grabado por tipo). texto llega vacío
// cuando el tipo aún no tiene nota guardada para este EdP; el box se dibuja igual, vacío.
const buildNotasItems = (data) =>
  (data?.notas ?? []).map((nota) => ({
    subtitulo: nota.tipoNombre,
    texto: nota.textoComentario ?? "",
  }));

function ChecklistPDF({ data, applicationRoles }) {
  // "GST" es el código de rol client-facing de AAD para AMSA (dbo.privilege_role.privlege_role_client_code),
  // no un texto fijo: se resuelve igual que en el resto del módulo de EdP (ResultadoEdp.js, DocumentFooter.jsx).
  const rolGst = __GetClientRoleCode(applicationRoles, enum__AppElementType.CONTRACT, "AAD");
  const mesNombre = data?.mes ? MESES[data.mes - 1] : "-";
  const checklistItems = getChecklistItems(data, rolGst);
  const notasItems = buildNotasItems(data);

  const camposIzquierda = [
    ["Contrato:", val(data?.contractCode)],
    ["Nombre del servicio:", val(data?.contractName)],
    ["EECC:", val(data?.eecc)],
    ["Estado de Pago Mes / Nº:", `${mesNombre} ${val(data?.anio)} / EP-${val(data?.nroEp)}`],
    [`Revisión Back Office ${rolGst}:`, data?.revisionBackOfficeGst ? "SI" : "NO"],
    ["AdC:", val(data?.adcCmz)],
    ["Gerencia:", val(data?.gerencia)],
    ["Mes:", mesNombre],
    ["Año:", val(data?.anio)],
    ["Fecha de Recepción:", formatFecha(data?.fechaRecepcion)],
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.logoRow}>
          <Image src={logoEmpresa} style={styles.logo} />
        </View>

        <View style={styles.banner}>
          <Text style={styles.bannerTitle}>Checklist Revisión y Verificación de Estados de Pago</Text>
          <Text style={styles.bannerSubtitle}>Back Office {rolGst} - {val(data?.leMandante)}</Text>
        </View>

        <View style={styles.headerBox}>
          <View style={styles.headerCol}>
            {camposIzquierda.map(([label, value]) => (
              <View style={styles.fieldRow} key={label}>
                <Text style={styles.fieldLabel}>{label}</Text>
                <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{value}</Text>
              </View>
            ))}
          </View>
          <View style={styles.headerColRight}>
            <Text style={styles.sectionTitle}>Información del servicio</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Criticidad:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.criticidad)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Monto del período:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{formatMonto(data?.montoPeriodo)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Moneda:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.moneda)}</Text>
            </View>

            <Text style={styles.sectionTitle}>Información de la revisión</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Asesor {rolGst}:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.asesorGst)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Revisión N°:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.revisionNumero)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fecha de envío:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{formatFecha(data?.fechaEnvioGst)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Estado EP:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.estadoEp)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.tableHeaderRow}>
          <Text style={[styles.tableHeaderCell, { flex: 4 }]}>Documentación</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1.1, textAlign: "center" }]}>Estatus</Text>
          <Text style={[styles.tableHeaderCell, { flex: 4 }]}>Observaciones</Text>
        </View>

        {checklistItems.map((item, index) => {
          const mostrarTablaIndices = item.documentacion === "Reajuste de tarifa" && data?.tieneReajuste && data?.indicesReajuste?.length > 0;

          return (
            <View key={`${item.documentacion}-${index}`} wrap={false}>
              <View style={styles.tableRow}>
                <Text style={styles.colDocumentacion} hyphenationCallback={(word) => [word]}>
                  {item.documentacion}
                </Text>
                <View style={styles.colEstatus}>
                  {item.estatus ? <EstatusIcon cumple={item.estatus === "Cumple"} /> : null}
                  <Text style={styles.estatusTexto}>{item.estatus}</Text>
                </View>
                <Text style={styles.colObservaciones} hyphenationCallback={(word) => [word]}>{item.observaciones}</Text>
              </View>

              {mostrarTablaIndices && (
                <View style={styles.indicesTable}>
                  <View style={styles.indicesHeaderRow}>
                    <Text style={styles.indicesHeaderCell}>Índice</Text>
                    <Text style={styles.indicesHeaderCell}>Valor Base</Text>
                    <Text style={styles.indicesHeaderCell} hyphenationCallback={(word) => [word]}>Fecha Valor Base</Text>
                    <Text style={styles.indicesHeaderCell}>Valor Actual</Text>
                    <Text style={styles.indicesHeaderCellLast} hyphenationCallback={(word) => [word]}>Fecha Valor Actual</Text>
                  </View>
                  {data.indicesReajuste.map((row, idx) => (
                    <View style={styles.indicesRow} key={`${row.indice}-${idx}`}>
                      <Text style={styles.indicesCell}>{val(row.indice)}</Text>
                      <Text style={styles.indicesCell}>{val(row.valorBase)}</Text>
                      <Text style={styles.indicesCell}>{formatFecha(row.fechaValorBase)}</Text>
                      <Text style={styles.indicesCell}>{val(row.valorActual)}</Text>
                      <Text style={styles.indicesCellLast}>{formatFecha(row.fechaValorActual)}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })}

        {notasItems.map((nota, index) => (
          <View key={`${nota.subtitulo}-${index}`} wrap={false}>
            {index === 0 && (
              <View style={styles.notasBanner}>
                <Text style={styles.notasBannerText}>Notas:</Text>
              </View>
            )}
            <Text style={styles.notaSubtitulo}>{nota.subtitulo}</Text>
            <View style={styles.notaBox}>
              {!!nota.texto && (
                <Text style={styles.notaBoxTexto} hyphenationCallback={(word) => [word]}>
                  {nota.texto}
                </Text>
              )}
            </View>
          </View>
        ))}

        <View wrap={false}>
          <View style={styles.notasBanner}>
            <Text style={styles.notasBannerText}>Firma Revisión</Text>
          </View>
          <View style={styles.firmaBox}>
            <Text style={styles.firmaRol}>{rolGst}</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Nombre:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.asesorGst)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>RUT:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.asesorGstUsername)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Cargo:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{val(data?.asesorGstCargo)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fecha:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{formatFecha(data?.aadFechaRevision)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Firma:</Text>
              <Text style={styles.fieldValue} hyphenationCallback={(word) => [word]}>{buildFirmaRevisionTexto(data)}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default ChecklistPDF;
